import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { Link } from "react-router-dom";
import { Button, Menu, MenuItem } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faUserTie } from "@fortawesome/free-solid-svg-icons";

import ConfirmationModal from "../../modal/ConfirmationModal";
import ConsultantEditModal from "./ConsultantEditModal";
import VenueChipsDisplay from "./VenueChipsDisplay";
import "./ConsultantContainer.css";

@inject("agentConsultantStore", "agentVenueStore", "templateStore", "userInvitationStore")
@observer
class ConsultantTable extends Component {
    state = {
        deletedAgentConsultant: undefined,
        deleteConfirmationState: false,
        agentConsultant: undefined,
        deleteInvitationState: false,
        editModalState: false,
        anchorEl: null,
        menuConsultant: null,
        menuType: null
    };

    openRemoveConfirmation = agentConsultant =>
        this.setState({ 
            deleteConfirmationState: true, 
            deletedAgentConsultant: agentConsultant,
            anchorEl: null 
        });
    
    closeRemoveConfirmation = () =>
        this.setState({
            deleteConfirmationState: false,
            deleteInvitationState: false,
            deletedAgentConsultant: undefined
        });

    openRemoveInvitationConfirmation = agentConsultant =>
        this.setState({ 
            deleteInvitationState: true, 
            deletedAgentConsultant: agentConsultant,
            anchorEl: null 
        });

    openEditModal = agentConsultant => 
        this.setState({ 
            editModalState: true, 
            agentConsultant: agentConsultant,
            anchorEl: null 
        });
    
    closeEditModal = () => 
        this.setState({ editModalState: false, agentConsultant: undefined });

    handleMenuOpen = (event, consultant, type) => {
        this.setState({
            anchorEl: event.currentTarget,
            menuConsultant: consultant,
            menuType: type
        });
    };

    handleMenuClose = () => {
        this.setState({
            anchorEl: null,
            menuConsultant: null,
            menuType: null
        });
    };

    handleRemove = () => {
        this.props.agentConsultantStore.removeConsultant(this.state.deletedAgentConsultant.id).then(response => {
            this.props.templateStore.openSnackbar(
                `Consultant ${
                    this.state.deletedAgentConsultant && this.state.deletedAgentConsultant.consultant
                        ? this.state.deletedAgentConsultant.consultant.firstName + " " + this.state.deletedAgentConsultant.consultant.lastName
                        : "selected consultant"
                } has been removed.`
            );
            this.closeRemoveConfirmation();
        });
    };

    handleRemoveInvitation = () => {
        this.props.userInvitationStore.deleteInvitation(this.state.deletedAgentConsultant).then(response => {
            this.props.templateStore.openSnackbar(
                `Consultant ${this.state.deletedAgentConsultant.invitedFirstName} ${this.state.deletedAgentConsultant.invitedLastName} - ${
                    this.state.deletedAgentConsultant.invitedEmail
                } has been removed.`
            );
            this.closeRemoveConfirmation();
        });
    };

    getInitials = (name) => {
        if (!name) return "?";
        const parts = name.trim().split(" ");
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    filterBySearch = (name, email) => {
        const { searchQuery } = this.props;
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            (name && name.toLowerCase().includes(query)) ||
            (email && email.toLowerCase().includes(query))
        );
    };

    renderActiveConsultants = () => {
        const { agentConsultants } = this.props.agentConsultantStore;
        const activeConsultants = agentConsultants.filter(x => x.accepted);
        
        const filteredConsultants = activeConsultants.filter(consultant => {
            const name = consultant.consultant 
                ? `${consultant.consultant.firstName} ${consultant.consultant.lastName}`
                : "";
            return this.filterBySearch(name, consultant.consultantEmail);
        });

        if (filteredConsultants.length === 0) {
            return this.renderEmptyState("active");
        }

        return (
            <div className="consultant-list">
                {filteredConsultants.map((consultant, index) => 
                    this.renderConsultantRow(consultant, index, "active")
                )}
            </div>
        );
    };

    renderPendingConsultants = () => {
        const { agentConsultants } = this.props.agentConsultantStore;
        const { invitations } = this.props.userInvitationStore;

        const pendingConsultants = agentConsultants.filter(x => !x.accepted);
        const filteredInvitations = invitations.filter(inv => 
            !agentConsultants.find(x => x?.consultantEmail === inv?.invitedEmail)
        );

        if (pendingConsultants.length === 0 && filteredInvitations.length === 0) {
            return this.renderEmptyState("pending");
        }

        return (
            <div className="consultant-list">
                {pendingConsultants.map((consultant, index) => 
                    this.renderConsultantRow(consultant, index, "pending")
                )}
                {filteredInvitations.map((invitation, index) => 
                    this.renderInvitationRow(invitation, index)
                )}
            </div>
        );
    };

    renderConsultantRow = (consultant, index, type) => {
        const { anchorEl, menuConsultant, menuType } = this.state;
        const name = consultant.consultant 
            ? `${consultant.consultant.firstName} ${consultant.consultant.lastName}`
            : consultant.consultantEmail;
        const email = consultant.consultantEmail;
        const avatarUrl = consultant.consultant?.profileImage?.imageProfile || 
                         consultant.consultant?.profileImage?.url200;
        const profileId = consultant.consultant?.id;

        const venues = consultant.consultantVenues?.map(cv => ({
            id: cv.id || cv.profileId || cv.venueId,
            profileName: cv.profileName || cv.fullName || cv.name
        })) || [];

        return (
            <div key={`consultant-${index}`} className="consultant-row">
                <div className="consultant-avatar">
                    {avatarUrl ? (
                        <img src={avatarUrl} alt={name} />
                    ) : (
                        this.getInitials(name)
                    )}
                </div>
                <div className="consultant-info">
                    <div className="consultant-name">
                        {profileId ? (
                            <Link to={`/profile/${profileId}`} target="_blank">
                                {name}
                            </Link>
                        ) : (
                            name
                        )}
                    </div>
                    <div className="consultant-email">{email}</div>
                </div>
                <VenueChipsDisplay venues={venues} maxVisible={10} />
                {type === "pending" && (
                    <span className="consultant-status-badge pending">Pending</span>
                )}
                <div className="consultant-actions-menu">
                    <Button
                        className="consultant-actions-btn"
                        onClick={(e) => this.handleMenuOpen(e, consultant, type)}
                    >
                        Actions
                        <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 10 }} />
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && menuConsultant === consultant && menuType === type}
                        onClose={this.handleMenuClose}
                    >
                        <MenuItem onClick={() => this.openEditModal(consultant)}>
                            Edit Venues
                        </MenuItem>
                        <MenuItem onClick={() => this.openRemoveConfirmation(consultant)}>
                            Remove Consultant
                        </MenuItem>
                    </Menu>
                </div>
            </div>
        );
    };

    renderInvitationRow = (invitation, index) => {
        const { anchorEl, menuConsultant, menuType } = this.state;
        const name = `${invitation.invitedFirstName} ${invitation.invitedLastName}`;
        const email = invitation.invitedEmail;

        const venues = invitation.venues?.map(v => ({
            id: v.id || v.profileId || v.venueId,
            profileName: v.profileName || v.fullName || v.name
        })) || [];

        return (
            <div key={`invitation-${index}`} className="consultant-row">
                <div className="consultant-avatar">
                    {this.getInitials(name)}
                </div>
                <div className="consultant-info">
                    <div className="consultant-name">{name}</div>
                    <div className="consultant-email">{email}</div>
                </div>
                <VenueChipsDisplay venues={venues} maxVisible={10} label="Assigned Venues" />
                <span className="consultant-status-badge invited">Invited</span>
                <div className="consultant-actions-menu">
                    <Button
                        className="consultant-actions-btn"
                        onClick={(e) => this.handleMenuOpen(e, invitation, "invitation")}
                    >
                        Actions
                        <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 10 }} />
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && menuConsultant === invitation && menuType === "invitation"}
                        onClose={this.handleMenuClose}
                    >
                        <MenuItem onClick={() => this.openRemoveInvitationConfirmation(invitation)}>
                            Cancel Invitation
                        </MenuItem>
                    </Menu>
                </div>
            </div>
        );
    };

    renderEmptyState = (type) => {
        if (type === "active") {
            return (
                <div className="consultant-empty-state">
                    <div className="consultant-empty-state-icon">
                        <FontAwesomeIcon icon={faUserTie} />
                    </div>
                    <h3>No active consultants</h3>
                    <p>
                        Add consultants to help manage your venues and bookings. 
                        They'll be able to access and manage the venues you assign to them.
                    </p>
                </div>
            );
        }
        return (
            <div className="consultant-empty-state">
                <div className="consultant-empty-state-icon">
                    <FontAwesomeIcon icon={faUserTie} />
                </div>
                <h3>No pending invitations</h3>
                <p>
                    All your consultant invitations have been accepted. 
                    Add more consultants to expand your team.
                </p>
            </div>
        );
    };

    render() {
        const { activeTab } = this.props;
        const msgRemove = (name) => {
            return `<span style="display:inline; font-weight:bold">${name}</span>`;
        };

        return (
            <Fragment>
                {activeTab === "active" ? this.renderActiveConsultants() : this.renderPendingConsultants()}

                <ConfirmationModal
                    open={this.state.deleteInvitationState}
                    message={`<p style="display:inline">Are you sure you would like to remove ${
                        this.state.deletedAgentConsultant 
                            ? msgRemove(this.state.deletedAgentConsultant.invitedFirstName + " " + this.state.deletedAgentConsultant.invitedLastName)
                            : "selected consultant"
                    } from your agency?</p>`}
                    closeHandler={this.closeRemoveConfirmation}
                    confirmationHandler={this.handleRemoveInvitation}
                    declineHandler={this.closeRemoveConfirmation}
                    confirmationLabel="Remove"
                />
                <ConfirmationModal
                    open={this.state.deleteConfirmationState}
                    message={`<p style="display:inline">Are you sure you want to remove ${
                        this.state.deletedAgentConsultant && this.state.deletedAgentConsultant.consultant
                            ? msgRemove(this.state.deletedAgentConsultant.consultant.firstName + " " + this.state.deletedAgentConsultant.consultant.lastName)
                            : "selected consultant"
                    } from your agency? This action cannot be undone.</p>`}
                    closeHandler={this.closeRemoveConfirmation}
                    confirmationHandler={this.handleRemove}
                    declineHandler={this.closeRemoveConfirmation}
                    confirmationLabel="Remove"
                />
                <ConsultantEditModal 
                    open={this.state.editModalState} 
                    onClose={this.closeEditModal} 
                    agentConsultant={this.state.agentConsultant} 
                />
            </Fragment>
        );
    }
}

export default ConsultantTable;
